import { z } from 'zod';
import { IsoDateSchema } from './common';

export const AgingBucketSchema = z.enum(['al-dia', '1-30', '31-60', '61-90', '90+']);
export type AgingBucket = z.infer<typeof AgingBucketSchema>;

export const TreasuryScopeQuery = z.object({
  scope: z.string().optional(),
});
export type TreasuryScopeQuery = z.infer<typeof TreasuryScopeQuery>;

export const AgingResponseSchema = z.object({
  total: z.number(),
  totalCount: z.number(),
  buckets: z.record(AgingBucketSchema, z.number()),
  counts: z.record(AgingBucketSchema, z.number()),
});
export type AgingResponse = z.infer<typeof AgingResponseSchema>;

export const TreasuryKpisSchema = z.object({
  bankBalance: z.number(),
  bankBalanceInitial: z.number(),
  collectedTotal: z.number(),
  manualDelta: z.number(),
  inflow30: z.number(),
  outflow30: z.number(),
  net30: z.number(),
  runwayMonths: z.number().nullable(),
  inflowInvoicesCount: z.number(),
});
export type TreasuryKpis = z.infer<typeof TreasuryKpisSchema>;

export const CashForecastBucketSchema = z.object({
  range: z.string(),
  from: z.number(),
  to: z.number(),
  inflow: z.number(),
  outflow: z.number(),
  net: z.number(),
});
export type CashForecastBucket = z.infer<typeof CashForecastBucketSchema>;

export const DsoResponseSchema = z.object({
  value: z.number(),
  target: z.number(),
  industry: z.number(),
  recommendation: z.string().nullable(),
});
export type DsoResponse = z.infer<typeof DsoResponseSchema>;

export const TopDebtorSchema = z.object({
  clientId: z.string().uuid(),
  clientName: z.string(),
  invoices: z.number(),
  balance: z.number(),
  maxOverdueDays: z.number(),
  bucket: AgingBucketSchema,
  creditLimit: z.number(),
  creditUsagePct: z.number(),
  paymentPrediction: z
    .object({
      days: z.number(),
      confidence: z.enum(['alta', 'media', 'baja']),
    })
    .nullable(),
});
export type TopDebtor = z.infer<typeof TopDebtorSchema>;

export const CashMovementTypeSchema = z.enum(['income', 'expense']);
export type CashMovementType = z.infer<typeof CashMovementTypeSchema>;

export const CASH_MOVEMENT_CATEGORIES = [
  'aportacion_socio',
  'cobro_sin_factura',
  'prestamo',
  'gasto_personal_pagado',
  'reembolso',
  'otros',
] as const;
export const CashMovementCategorySchema = z.enum(CASH_MOVEMENT_CATEGORIES);
export type CashMovementCategory = z.infer<typeof CashMovementCategorySchema>;

export const CashMovementSchema = z.object({
  id: z.string().uuid(),
  type: CashMovementTypeSchema,
  amount: z.number(),
  date: IsoDateSchema,
  category: z.string(),
  description: z.string().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdByCode: z.string().nullable(),
  createdAt: z.string(),
});
export type CashMovement = z.infer<typeof CashMovementSchema>;

export const CreateCashMovementSchema = z.object({
  type: CashMovementTypeSchema,
  amount: z.number().positive(),
  date: IsoDateSchema,
  category: CashMovementCategorySchema.default('otros'),
  description: z.string().max(500).nullish(),
});
export type CreateCashMovementRequest = z.infer<typeof CreateCashMovementSchema>;

export const UpdateCashMovementSchema = z.object({
  type: CashMovementTypeSchema.optional(),
  amount: z.number().positive().optional(),
  date: IsoDateSchema.optional(),
  category: CashMovementCategorySchema.optional(),
  description: z.string().max(500).nullish(),
});
export type UpdateCashMovementRequest = z.infer<typeof UpdateCashMovementSchema>;
